import { MobileOnlyLogo } from '@/pages/components/Branding';
import { FormTitleAndLogo } from '@/pages/components/Form';
import { FormElements } from '@/pages/components/form-fields/FormType';
import Input from '@/pages/components/form-fields/input/Input';
import { LocationInput } from '@/pages/components/form-fields/input/LocationInput';
import PhoneNumber from '@/pages/components/form-fields/phoneNumber/PhoneNumber';
import { Title } from '@/pages/components/Form.styles';
import { MultiSelect } from '@/pages/components/MultiSelect/MultiSelect';
import { Link, Typography } from '@montugroup/design-system';
import type { Step } from '@montugroup/form-builder';
import { ContentDisplay, BREADCRUMB_STATUS as STATUS } from '@montugroup/form-builder';
import type { ReactNode } from 'react';
import {
  medicalConditions,
  medicationDosage,
  medicationFrequency,
  medications,
  treatmentDurations,
} from './medication-data';

const getFormTitle = (showLogo: boolean) => {
  if (!showLogo) {
    return (
      <Title variant="h4" component="h1">
        Pre-consult Form
      </Title>
    );
  }

  return <FormTitleAndLogo>Pre-consult Form</FormTitleAndLogo>;
};

const BREADCRUMBS = {
  PERSONAL_DETAILS: 'Personal details',
  CURRENT_HEALTH_OVERVIEW: 'Current health overview',
  PAST_MEDICATIONS: 'Past medications',
};

const breadcrumbPersonalDetails = [
  { label: BREADCRUMBS.PERSONAL_DETAILS, status: STATUS.CURRENT },
  {
    label: BREADCRUMBS.CURRENT_HEALTH_OVERVIEW,
    status: STATUS.UPCOMING,
  },
  { label: BREADCRUMBS.PAST_MEDICATIONS, status: STATUS.UPCOMING },
];

const breadcrumbHealthOverview = [
  { label: BREADCRUMBS.PERSONAL_DETAILS, status: STATUS.COMPLETE },
  {
    label: BREADCRUMBS.CURRENT_HEALTH_OVERVIEW,
    status: STATUS.CURRENT,
  },
  { label: BREADCRUMBS.PAST_MEDICATIONS, status: STATUS.UPCOMING },
];

const breadcrumbPastMedications = [
  { label: BREADCRUMBS.PERSONAL_DETAILS, status: STATUS.COMPLETE },
  {
    label: BREADCRUMBS.CURRENT_HEALTH_OVERVIEW,
    status: STATUS.COMPLETE,
  },
  { label: BREADCRUMBS.PAST_MEDICATIONS, status: STATUS.CURRENT },
];

// Optional step for when we don't have access to the email they already provided us
export const getOptionalEmailCollectionStep = (showLogo: boolean): Step =>
  ({
    id: 0,
    type: 'question',
    qid: 'email',
    formTitle: getFormTitle(showLogo),
    title: 'What is your email address?',
    subtitle: 'Please confirm your email address used when signing up',
    breadcrumbs: breadcrumbPersonalDetails,
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Email',
      clickId: 'email',
    },
    content: [{ type: 'email', label: 'Email address' }],
  }) as Step;

export const getPreConsultationSteps = (showLogo: boolean): Step[] => [
  {
    id: 1,
    type: 'section',
    formTitle: showLogo ? <MobileOnlyLogo /> : null,
    nextButtonConfig: {
      text: 'Get Started',
      metadata: {
        id: 'get-started',
      },
    },
    trackingData: {
      event: 'preconsult_start',
      stepName: 'Get Started',
      clickId: 'get-started',
    },
    content: (
      <>
        <Typography variant="h5" component="h1" sx={{ mb: 10 }} fontWeight="bold">
          Hello,
        </Typography>
        <Typography variant="h5" paragraph sx={{ mb: 10 }}>
          To ensure you receive the best possible care, we&apos;ll ask a few questions that help our clinicians
          understand and support your health needs.
        </Typography>
        <Typography variant="h5" paragraph sx={{ mb: 10 }}>
          All information gathered will remain confidential and will be handled in line with the Alternaleaf{' '}
          <Link href="https://www.alternaleaf.com.au/privacy" target="_blank" rel="noopener noreferrer">
            privacy policy
          </Link>
          .
        </Typography>
        <Typography variant="h5" paragraph sx={{ mb: 10 }}>
          This will take about 5 minutes. Your answers will only be submitted to your clinicians once you finish the
          entire form. If you&apos;re short on time, you can exit now and return when you&apos;re ready.
        </Typography>
      </>
    ),
  } as Step,
  {
    id: 2,
    type: 'question',
    qid: 'gender',
    formTitle: getFormTitle(showLogo),
    title:
      'What is your gender as it appears on your medicare records or other official documentation (such as your passport)?',
    subtitle: 'We need this information to connect to your health records and process your prescription.',
    breadcrumbs: breadcrumbPersonalDetails,
    nextButtonConfig: {
      metadata: {
        id: 'gender',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Gender',
      clickId: 'gender',
    },
    content: [
      {
        type: 'radio',
        values: [
          {
            id: 1,
            label: 'Male',
          },
          {
            id: 2,
            label: 'Female',
          },
          {
            id: 3,
            label: 'Non-binary',
          },
        ],
      },
    ],
  } as Step,
  {
    id: 3,
    type: 'question',
    qid: 'birthday',
    formTitle: getFormTitle(showLogo),
    title: 'What is your date of birth?*',
    subtitle: 'This is used to verify your identity and link your records with your IHI number.',
    breadcrumbs: breadcrumbPersonalDetails,
    nextButtonConfig: {
      metadata: {
        id: 'dob',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Date of birth',
      clickId: 'dob',
    },
    content: [
      {
        type: 'date',
        name: 'birthday',
        label: 'Date of Birth',
        legalAge: true,
      },
    ],
  } as Step,
  {
    id: 4,
    type: 'question',
    qid: 'address',
    formTitle: getFormTitle(showLogo),
    title: 'What is your home address?*',
    subtitle: 'This is used to verify your identity and link your records with your IHI number.',
    breadcrumbs: breadcrumbPersonalDetails,
    nextButtonConfig: {
      metadata: {
        id: 'home-address',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Home address',
      clickId: 'home-address',
    },
    fieldArray: true,
    content: [
      {
        type: 'custom',
        name: 'streetAndHouseNo',
        component: <LocationInput name="address.streetAndHouseNo" label="Street and house number" />,
      },
      {
        name: 'suburb',
        type: 'input',
        label: 'Suburb',
        autofill: 'Los Angeles',
      },
      {
        name: 'state',
        type: 'select',
        label: 'State',
        options: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
      },
      {
        name: 'postcode',
        type: 'number',
        label: 'Postcode',
        inputProps: {
          format: '####',
        },
        autofill: '2267',
      },
    ],
  } as Step,
  {
    id: 5,
    type: 'question',
    qid: 'emergencyContact',
    formTitle: getFormTitle(showLogo),
    title: 'Who can we contact in case of an emergency?*',
    breadcrumbs: breadcrumbPersonalDetails,
    nextButtonConfig: {
      metadata: {
        id: 'emergency-contact',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Emergency contact',
      clickId: 'emergency-contact',
    },
    fieldArray: true,
    content: [
      { name: 'firstName', type: 'input', label: 'First name', autofill: 'Voletta' },
      { name: 'lastName', type: 'input', label: 'Last name', autofill: 'Wallace' },
      { name: 'relationship', type: 'input', label: 'Relationship', autofill: 'Mother' },
      {
        type: 'custom',
        name: 'phoneNo',
        component: (
          <PhoneNumber
            name="emergencyContact.phoneNo"
            type={FormElements.phoneNumber}
            label="Phone Number"
            autofill="+61 0421 111 222"
            countrySelect
            requiredText="Phone number required"
            invalidNumberText="Please enter a valid mobile phone number or landline number."
          />
        ),
      },
    ],
  } as Step,
  {
    id: 6,
    type: 'question',
    qid: 'has_medicare',
    formTitle: getFormTitle(showLogo),
    title: 'Do you have a medicare card?',
    subtitle: 'We need this information to connect to your health records and process your prescription.',
    breadcrumbs: breadcrumbPersonalDetails,
    nextButtonConfig: {
      metadata: {
        id: 'medicare-details',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Medicare availability',
      clickId: 'medicare-details',
    },
    content: [
      {
        type: 'radio',
        values: [
          {
            id: 1,
            label: 'Yes',
            value: true,
          },
          {
            id: 2,
            label: 'No',
            value: false,
          },
        ],
      },
    ],
    skipTo: (nextStepIndex: number, values: { [key: string]: string }) => {
      if (values['has_medicare'] === 'false') {
        return nextStepIndex + 1; // Skip the medicare details step
      }
      return nextStepIndex;
    },
  } as Step,
  {
    id: 7,
    type: 'question',
    qid: 'medicare',
    formTitle: getFormTitle(showLogo),
    title: 'Medicare Details*',
    subtitle: 'Medicare information is needed to generate e-prescriptions. \n\n ![](/assets/medicare.png)',
    breadcrumbs: breadcrumbPersonalDetails,
    nextButtonConfig: {
      metadata: {
        id: 'medicare-details',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Medicare details',
      clickId: 'medicare-details',
    },
    fieldArray: true,
    content: [
      {
        type: 'custom',
        name: 'number',
        component: (
          <Input
            name="medicare.number"
            type={FormElements.medicareNumber}
            label={'1. Medicare number'}
            autofill={'2234567891'}
            inputProps={{ format: '#### ##### #' }}
            helperText={'The 10 digit number at the top of your card'}
          />
        ),
      },
      {
        name: 'referenceNo',
        type: 'number',
        label: '2. Medicare reference number',
        helperText: 'The single digit number along the left side',
        inputProps: { format: '#' },
        autofill: '1',
      },
      {
        name: 'fullName',
        type: 'input',
        label: '3. Name as it appears on your card',
        autofill: 'Christopher George Latore Wallace',
      },
      {
        name: 'expiryDate',
        type: 'date',
        label: '4. Expiry date',
        helperText: 'The date in MM/YYYY format on the bottom right of your card',
        inputProps: {
          format: 'MM/yyyy',
          views: ['year', 'month'],
          disablePast: true,
        },
      },
    ],
  } as Step,
  {
    id: 8,
    type: 'question',
    qid: 'healthConcerns',
    formTitle: getFormTitle(showLogo),
    title: 'Please select a condition(s) from the list below that you want to discuss with a specialist*',
    breadcrumbs: breadcrumbHealthOverview,
    nextButtonConfig: {
      metadata: {
        id: 'chronic-conditions',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Specify your chronic condition',
      clickId: 'chronic-conditions',
    },
    content: [
      {
        type: 'custom',
        name: 'concerns',
        component: (
          <MultiSelect label="Search for conditions" name="healthConcerns.concerns" options={medicalConditions} />
        ) as ReactNode,
      },
    ],
  } as Step,
  {
    id: 9,
    type: 'question',
    qid: 'primaryConcern',
    contentDisplay: ContentDisplay.FLEX_COLUMN,
    formTitle: getFormTitle(showLogo),
    title: 'Please select the condition that is your primary health concern*',
    breadcrumbs: breadcrumbHealthOverview,
    enableNextOnlyWhenValid: true,
    nextButtonConfig: {
      metadata: {
        id: 'primary-concern',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Primary health concern',
      clickId: 'primary-concern',
    },
    content: [
      {
        type: 'radio',
        name: 'primaryConcern',
        valuesFrom: 'healthConcerns.concerns',
        subOptions: [
          {
            label: 'How long have you been experiencing this for?',
            name: 'duration',
            options: treatmentDurations,
          },
        ],
      },
    ],
  } as unknown as Step,
  {
    id: 10,
    type: 'question',
    qid: 'currentMedications',
    formTitle: getFormTitle(showLogo),
    title: 'What medications are you currently taking?',
    breadcrumbs: breadcrumbHealthOverview,
    nextButtonConfig: {
      metadata: {
        id: 'current-medication',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Current Medication',
      clickId: 'current-medication',
    },
    content: [
      {
        type: 'custom',
        name: 'medication',
        component: (
          <MultiSelect
            label="Search for current medications"
            name="currentMedications.medication"
            required={false}
            options={medications}
            subOptions={[
              {
                label: 'Dosage',
                name: 'dosage',
                options: medicationDosage,
              },
              {
                label: 'Frequency',
                name: 'frequency',
                options: medicationFrequency,
              },
            ]}
          />
        ),
      },
    ],
  } as Step,
  {
    id: 11,
    type: 'question',
    qid: 'previousMedications',
    formTitle: getFormTitle(showLogo),
    title: 'What medications have you previously trialled to address your current health conditions?',
    breadcrumbs: breadcrumbPastMedications,
    nextButtonConfig: {
      metadata: {
        id: 'treatments-tried',
      },
    },
    trackingData: {
      event: 'preconsult_progress',
      stepName: 'Treatments tried',
      clickId: 'treatments-tried',
    },
    content: [
      {
        type: 'custom',
        name: 'medication',
        component: (
          <MultiSelect
            label="Search for previous medications"
            name="previousMedications.medication"
            required={false}
            options={medications}
            subOptions={[
              {
                label: 'Dosage',
                name: 'dosage',
                options: medicationDosage,
              },
              {
                label: 'Frequency',
                name: 'frequency',
                options: medicationFrequency,
              },
            ]}
          />
        ),
      },
    ],
  } as Step,
  {
    id: 12,
    type: 'section',
    formTitle: showLogo ? <MobileOnlyLogo /> : null,
    shouldSave: true,
    hideBackButton: true,
    content: (
      <>
        <Typography variant="h5" component="h1" sx={{ mb: 10 }} fontWeight="bold">
          Thank you,
        </Typography>
        <Typography variant="h5" paragraph sx={{ mb: 10 }}>
          You have successfully completed the pre-consult forms and your answers have been securely sent to your
          Alternaleaf clinicians.
        </Typography>
      </>
    ),
    nextButtonConfig: {
      text: 'Rate your experience',
      href: 'https://g.page/r/CQdeugyygLF4EAI/review',
    },
  } as Step,
];
